<script setup>
import {computed, nextTick, onBeforeUnmount, ref, watch} from "vue";
import AppIcon from "@/components/AppIcon.vue";

const props = defineProps(['show', 'title'])
const emit = defineEmits(['update:show'])

const dialog = ref(null)
const titleId = 'ww-modal-title-' + Math.random().toString(36).slice(2, 9)
let lastFocused = null

const showValue = computed({
  get() {
    return props.show
  },
  set(value) {
    emit('update:show', value)
  }
})

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusables() {
  return dialog.value ? [...dialog.value.querySelectorAll(FOCUSABLE)] : []
}

function onKeydown(event) {
  if (event.key === 'Escape') {
    showValue.value = false

    return
  }

  if (event.key !== 'Tab') {
    return
  }

  // Keep Tab inside the dialog, otherwise focus walks the page behind it.
  const items = focusables()
  if (!items.length) {
    event.preventDefault()

    return
  }

  const first = items[0]
  const last = items[items.length - 1]
  const active = document.activeElement

  if (event.shiftKey && (active === first || !dialog.value.contains(active))) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

function release() {
  document.removeEventListener('keydown', onKeydown)
}

// The action that closes the dialog often disables its own trigger (a door
// command starts loading), and .focus() on a disabled element is a silent
// no-op that drops focus to <body>. Land on the surrounding card instead.
function restoreFocus() {
  const opener = lastFocused
  lastFocused = null

  if (!opener || !document.contains(opener)) {
    return
  }

  if (!opener.disabled) {
    opener.focus()

    return
  }

  const fallback = opener.closest('section, article, main')
  if (!fallback) {
    return
  }

  fallback.setAttribute('tabindex', '-1')
  fallback.focus()
}

watch(showValue, async isOpen => {
  if (isOpen) {
    lastFocused = document.activeElement
    document.addEventListener('keydown', onKeydown)
    await nextTick()
    ;(focusables()[0] ?? dialog.value)?.focus()

    return
  }

  release()
  restoreFocus()
})

onBeforeUnmount(release)
</script>

<template>
  <teleport to="body">
    <transition name="ww-fade">
      <div
          v-if="showValue"
          class="ww-modal__backdrop"
          @click.self="showValue = false"
      >
        <div
            class="ww-modal"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="titleId"
            tabindex="-1"
            ref="dialog"
        >
          <header class="ww-modal__header">
            <h2 class="ww-modal__title" :id="titleId">{{ props.title }}</h2>
            <button
                type="button"
                class="ww-modal__close"
                :aria-label="$t('closeDialog')"
                @click="showValue = false"
            >
              <app-icon name="close" :size="20"/>
            </button>
          </header>

          <div class="ww-modal__content ww-scroll">
            <slot name="content"></slot>
          </div>

          <footer v-if="$slots.footer" class="ww-modal__footer">
            <slot name="footer"></slot>
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.ww-modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 18px;
  background: rgba(20, 28, 40, 0.55);
  backdrop-filter: blur(2px);
}

.ww-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  max-height: 100%;
  background: var(--ww-surface);
  border-radius: var(--ww-r-xl);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  overflow: hidden;
}

.ww-modal:focus {
  outline: none;
}

.ww-modal__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 20px 12px;
}

.ww-modal__title {
  flex: 1 1 auto;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ww-text);
}

.ww-modal__close {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: var(--ww-surface-sub-alt);
  color: var(--ww-text-muted);
  flex: 0 0 auto;
}

.ww-modal__close:hover {
  background: var(--ww-border);
}

.ww-modal__content {
  padding: 0 20px 20px;
  overflow-y: auto;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--ww-text-body);
}

.ww-modal__content :deep(p) {
  margin: 0;
}

.ww-modal__footer {
  padding: 16px 20px;
  background: var(--ww-surface-sub);
  border-top: 1px solid var(--ww-border-line);
}
</style>
